/**
 * What the source actually supplied, per statement.
 *
 * This answers the question the findings list never does: not "what is wrong"
 * but "what did we get". Two rules carry the meaning, both inherited from the
 * table this replaces:
 *
 *   - `available: false` is NOT an error. The source simply never supplied that
 *     statement, so it is listed in words below the chart rather than drawn as
 *     a zero-length bar. A red bar would imply a failure that did not happen,
 *     and a zero bar would imply the statement was expected and came back empty.
 *   - `defaulted > 0` IS worth flagging. Those are zeros standing in for missing
 *     values: they look like real data and silently corrupt any ratio built on
 *     them.
 *
 * The `total` a statement reports can exceed reported + derived + defaulted,
 * because the backend counts only three of its seven field classifications into
 * those buckets. The remainder is drawn as "other" rather than folded into one
 * of the three or quietly dropped.
 */

import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartCard from '../charts/ChartCard';
import {
  BAR_MAX_SIZE,
  CHART_INK,
  CHART_TYPE,
  gridProps,
  legendProps,
  xAxisProps,
} from '../charts/chartTheme';
import Tooltip from '../../ui/Tooltip';
import type { ValidationStatement } from '../../../types/dataQuality';
import { ORIGIN_TOKENS } from './dqTokens';

export interface DataSupplyChartProps {
  statements: ValidationStatement[] | null | undefined;
}

const SERIES = [
  { key: 'reported', label: 'Reported', color: ORIGIN_TOKENS.reported.hex },
  { key: 'derived', label: 'Derived', color: ORIGIN_TOKENS.derived.hex },
  { key: 'defaulted', label: 'Defaulted to zero', color: ORIGIN_TOKENS.defaulted.hex },
  // The remainder the backend does not classify into the three buckets above.
  { key: 'other', label: 'Other', color: ORIGIN_TOKENS.absent.hex },
] as const;

function SupplyTable({ rows }: { rows: ValidationStatement[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[24rem] text-left text-xs">
        <thead>
          <tr className="border-b border-neutral-200 text-neutral-500">
            <th className="py-1.5 pr-2 font-medium">Statement</th>
            <th className="py-1.5 pr-2 font-medium">Status</th>
            <th className="py-1.5 pr-2 text-right font-medium">Reported</th>
            <th className="py-1.5 pr-2 text-right font-medium">Derived</th>
            <th className="py-1.5 pr-2 text-right font-medium">Defaulted</th>
            <th className="py-1.5 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (!row.available) {
              return (
                <tr
                  key={row.statement}
                  className="border-b border-neutral-100 text-neutral-400 last:border-b-0"
                >
                  <td className="py-1.5 pr-2">{row.label}</td>
                  <td className="py-1.5" colSpan={5}>
                    not supplied by this source
                  </td>
                </tr>
              );
            }
            return (
              <tr key={row.statement} className="border-b border-neutral-100 last:border-b-0">
                <td className="py-1.5 pr-2 text-neutral-800">{row.label}</td>
                {/* The positive confirmation that the statement arrived at all
                    — the question the three count columns never answer. */}
                <td className="py-1.5 pr-2">
                  <span className="inline-flex items-center gap-1 text-neutral-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    supplied
                  </span>
                </td>
                <td className="py-1.5 pr-2 text-right font-mono tabular-nums text-neutral-800">
                  {row.reported}
                </td>
                <td className="py-1.5 pr-2 text-right font-mono tabular-nums text-neutral-600">
                  {row.derived}
                </td>
                <td className="py-1.5 pr-2 text-right">
                  {row.defaulted > 0 ? (
                    <Tooltip
                      content={`${row.defaulted} missing value${
                        row.defaulted === 1 ? '' : 's'
                      } replaced with zero — these can distort any ratio built on them`}
                      position="top"
                    >
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono font-semibold tabular-nums text-amber-800">
                        {row.defaulted}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="font-mono tabular-nums text-neutral-400">0</span>
                  )}
                </td>
                <td className="py-1.5 text-right font-mono tabular-nums text-neutral-500">
                  {row.total}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function DataSupplyChart({ statements }: DataSupplyChartProps) {
  const [asTable, setAsTable] = useState(false);

  const rows = (statements ?? []).filter(Boolean);
  if (rows.length === 0) return null;

  const supplied = rows.filter((r) => r.available);
  const missing = rows.filter((r) => !r.available);

  const data = supplied.map((row) => {
    const other = Math.max(0, (row.total || 0) - row.reported - row.derived - row.defaulted);
    return {
      label: row.label,
      reported: row.reported,
      derived: row.derived,
      defaulted: row.defaulted,
      other,
    };
  });

  return (
    <ChartCard
      title="What this source supplied"
      subtitle="Fields per statement in the most recent reporting period"
      action={
        <button
          type="button"
          onClick={() => setAsTable((t) => !t)}
          className="rounded-md border border-neutral-200 px-2 py-1 text-xs text-neutral-600 transition-colors hover:border-neutral-300 hover:text-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500"
        >
          {asTable ? 'Show chart' : 'Show table'}
        </button>
      }
      footnote={
        missing.length > 0 ? (
          <p className="mt-3 text-xs text-neutral-500">
            Not supplied by this source: {missing.map((m) => m.label).join(', ')}. An absence,
            not a failure — the source simply does not carry{' '}
            {missing.length === 1 ? 'it' : 'them'}.
          </p>
        ) : undefined
      }
    >
      {asTable ? (
        <SupplyTable rows={rows} />
      ) : (
        <div style={{ height: Math.max(160, data.length * 46 + 60) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid {...gridProps} horizontal={false} />
              <XAxis type="number" allowDecimals={false} {...xAxisProps} />
              <YAxis
                type="category"
                dataKey="label"
                width={110}
                tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }}
                tickLine={false}
                axisLine={false}
              />
              <RechartsTooltip
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                contentStyle={{ fontSize: CHART_TYPE.tooltip, borderRadius: 8 }}
              />
              <Legend {...legendProps} />
              {SERIES.map((series) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.label}
                  stackId="supply"
                  fill={series.color}
                  maxBarSize={BAR_MAX_SIZE}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
}
